import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiZap, FiUpload, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  padding: 24px 24px 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalDescription = styled.p`
  font-size: 10px;
  color: #6b7280;
  margin: 12px 0 0 0;
  padding: 0 24px;
  line-height: 1.5;
`;

const PromptContainer = styled.div`
  padding: 24px;
`;

const PromptLabel = styled.label`
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const PromptTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 10px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 10px;
  }
`;

const ExamplesContainer = styled.div`
  padding: 0 24px 24px 24px;
`;

const ExamplesTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
`;

const ExamplesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ExampleItem = styled.button`
  text-align: left;
  padding: 12px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid transparent;

  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;

    &:hover:not(:disabled) {
      background: #2563eb;
      border-color: #2563eb;
    }
  ` : `
    background: white;
    color: #374151;
    border-color: #d1d5db;

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  padding: 60px 24px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  color: #6b7280;
  font-size: 16px;
  margin: 0;
`;

const ModeSelector = styled.div`
  padding: 24px 24px 0 24px;
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModeButton = styled.button`
  padding: 12px 24px;
  border: 2px solid transparent;
  border-radius: 8px 8px 0 0;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.active ? 'white' : '#f9fafb'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  border-bottom-color: ${props => props.active ? 'white' : 'transparent'};

  &:hover {
    background: ${props => props.active ? 'white' : '#f3f4f6'};
    color: ${props => props.active ? '#3b82f6' : '#374151'};
  }
`;

const FileUploadContainer = styled.div`
  padding: 24px;
`;

const FileUploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  background: ${props => props.uploading ? '#fef3c7' : '#fafafa'};
  border-color: ${props => props.uploading ? '#f59e0b' : props.dragover ? '#3b82f6' : '#d1d5db'};

  &:hover {
    border-color: #3b82f6;
    background: #f8faff;
  }
`;

const FileUploadIcon = styled.div`
  font-size: 48px;
  color: ${props => props.uploading ? '#f59e0b' : '#9ca3af'};
  margin-bottom: 16px;
`;

const FileUploadText = styled.p`
  color: #374151;
  font-size: 16px;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const FileUploadSubtext = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadedFileInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FileIcon = styled.div`
  font-size: 24px;
  color: #0369a1;
`;

const FileDetails = styled.div`
  flex: 1;
`;

const FileName = styled.p`
  color: #0c4a6e;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const FileSize = styled.p`
  color: #0369a1;
  font-size: 12px;
  margin: 0;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: #fef2f2;
  }
`;

function AIGeneratorModal({ isOpen, onClose, onGenerateForm, initialMode = 'text' }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(initialMode); // 'text' or 'file'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Update uploadMode when initialMode changes
  React.useEffect(() => {
    setUploadMode(initialMode);
  }, [initialMode]);

  // Cleanup function to reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setPrompt('');
      setLoading(false);
      setUploadMode('text');
      setUploadedFile(null);
      setUploading(false);
    }
  }, [isOpen]);

  // Examples removed per user request

  // Generate form from processed text
  const generateFormFromText = async (text) => {
    try {
      setLoading(true);
      
      console.log('🚀 Starting AI form generation...');
      console.log('📝 Prompt:', text);
      console.log('📏 Prompt length:', text.length);
      
      // Use backend AI generation service
      let backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Remove trailing /api if it exists to avoid double /api/
      if (backendUrl.endsWith('/api')) {
        backendUrl = backendUrl.replace('/api', '');
      }
      
      const requestUrl = `${backendUrl}/api/ai/generate-form`;
      
      console.log('🌐 Making AI generation request to:', requestUrl);
      console.log('🔑 API URL from env:', process.env.REACT_APP_API_URL);
      console.log('📦 Request body:', JSON.stringify({ prompt: text }));
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: text }),
      });
      
      console.log('📡 AI Response status:', response.status);
      console.log('📡 AI Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ AI Response error text:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || `AI generation failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ AI Response received:', result);
      console.log('📊 Response data type:', typeof result.data);
      console.log('📊 Response data is array:', Array.isArray(result.data));
      console.log('📊 Response source:', result.source);
      
      if (!result.success) {
        throw new Error(result.error || 'AI generation failed');
      }

      // Backend now returns result.data as an object with title, description, and form_structure
      // Handle both old format (array) and new format (object)
      let formStructure;
      
      if (Array.isArray(result.data)) {
        // Old format: result.data is an array of fields
        const fields = result.data;
        if (!fields || fields.length === 0) {
          throw new Error('AI generated empty or invalid form structure');
        }
        formStructure = {
          title: extractTitle(text) || `AI Generated Form - ${new Date().toLocaleDateString()}`,
          description: extractDescription(text) || `Generated from: "${text.substring(0, 100)}${text.length > 100 ? '...' : ''}"`,
          form_structure: fields,
          logo_url: ''
        };
      } else {
        // New format: result.data is an object with title, description, form_structure
        const fields = result.data?.form_structure || [];
        if (!fields || fields.length === 0) {
          throw new Error('AI generated empty or invalid form structure');
        }
        formStructure = {
          title: result.data.title || extractTitle(text) || `AI Generated Form - ${new Date().toLocaleDateString()}`,
          description: result.data.description || extractDescription(text) || `Generated from: "${text.substring(0, 100)}${text.length > 100 ? '...' : ''}"`,
          form_structure: fields,
          logo_url: result.data.logo_url || ''
        };
      }

      console.log('✅ AI generation successful!');
      console.log('📋 Generated fields:', formStructure.form_structure.length);
      console.log('📋 Field details:', formStructure.form_structure.map(f => ({ type: f.type, label: f.label })));
      console.log('📋 Form title:', formStructure.title);
      console.log('📋 Form description:', formStructure.description);
      
      // Call the onGenerateForm callback with the generated form and prompt
      if (onGenerateForm) {
        console.log('🔄 Calling onGenerateForm callback...');
        onGenerateForm(formStructure, text);
        onClose();
        toast.success('Form generated successfully with AI!');
      } else {
        console.error('❌ onGenerateForm callback is not defined!');
      }
      
    } catch (error) {
      console.error('❌ Error generating form from text:', error);
      console.error('❌ Error stack:', error.stack);
      toast.error(`Failed to generate form: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'text/plain' // .txt
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a Word document (.doc, .docx) or text file (.txt)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    setUploadedFile(file);

    try {
      // Use backend Word processing service
      const formData = new FormData();
      formData.append('file', file);

      // Use absolute URL to ensure we're hitting the backend
      let backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Remove trailing /api if it exists to avoid double /api/
      if (backendUrl.endsWith('/api')) {
        backendUrl = backendUrl.replace('/api', '');
      }
      
      const requestUrl = `${backendUrl}/api/word/process`;
      
      console.log('Making request to:', requestUrl);
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        body: formData,
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const htmlText = await response.text();
        console.error('Received HTML instead of JSON:', htmlText.substring(0, 200));
        throw new Error('Server returned HTML instead of JSON. Please check if the backend server is running.');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to process document');
      }

      const { text, processingMessages, wordCount } = result.data;

      if (!text.trim()) {
        toast.error('No text content found in the document');
        setUploadedFile(null);
        return;
      }

      // Don't set the prompt - just use it for generation
      // setPrompt(text); // Removed - don't show full content in description
      
      // Show success message with additional info
      let successMessage = `Document processed successfully! (${wordCount} words)`;
      if (processingMessages && processingMessages.length > 0) {
        console.warn('Document processing warnings:', processingMessages);
        successMessage += ' (Some formatting may have been lost)';
      }
      
      // Automatically generate the form after processing the Word file
      console.log('Auto-generating form from processed text...');
      await generateFormFromText(text);
      
      toast.success(successMessage);
      
    } catch (error) {
      console.error('Error processing file:', error);
      
      // Fallback to client-side processing for text files
      if (file.type === 'text/plain') {
        try {
          console.log('Attempting client-side text file processing...');
          const text = await file.text();
          if (text.trim()) {
            // Don't set prompt - just generate the form
            await generateFormFromText(text);
            toast.success('Document processed successfully (using fallback method)!');
            return;
          }
        } catch (fallbackError) {
          console.error('Fallback processing failed:', fallbackError);
        }
      }
      
      toast.error(`Failed to process document: ${error.message}`);
      setUploadedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = React.useCallback(async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description for your form');
      return;
    }

    setLoading(true);
    try {
      // Use backend AI generation service
      let backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Remove trailing /api if it exists to avoid double /api/
      if (backendUrl.endsWith('/api')) {
        backendUrl = backendUrl.replace('/api', '');
      }
      
      const requestUrl = `${backendUrl}/api/ai/generate-form`;
      
      console.log('🌐 Making AI generation request to:', requestUrl);
      console.log('🔑 API URL from env:', process.env.REACT_APP_API_URL);
      console.log('📦 Request body:', JSON.stringify({ prompt }));
      
      // Call the real AI service
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt })
      });

      if (!response.ok) {
        throw new Error(`AI service error: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'AI generation failed');
      }

      // Backend now returns result.data as an object with title, description, and form_structure
      // Handle both old format (array) and new format (object)
      let formStructure;
      
      if (Array.isArray(result.data)) {
        // Old format: result.data is an array of fields
        const fields = result.data;
        if (!fields || fields.length === 0) {
          throw new Error('AI generated empty or invalid form structure');
        }
        formStructure = {
          title: extractTitle(prompt) || `AI Generated Form - ${new Date().toLocaleDateString()}`,
          description: extractDescription(prompt) || `Generated from: "${prompt.substring(0, 100)}${prompt.length > 100 ? '...' : ''}"`,
          form_structure: fields,
          logo_url: ''
        };
      } else {
        // New format: result.data is an object with title, description, form_structure
        const fields = result.data?.form_structure || [];
        if (!fields || fields.length === 0) {
          throw new Error('AI generated empty or invalid form structure');
        }
        formStructure = {
          title: result.data.title || extractTitle(prompt) || `AI Generated Form - ${new Date().toLocaleDateString()}`,
          description: result.data.description || extractDescription(prompt) || `Generated from: "${prompt.substring(0, 100)}${prompt.length > 100 ? '...' : ''}"`,
          form_structure: fields,
          logo_url: result.data.logo_url || ''
        };
      }
      
      // Debug logging to help troubleshoot field generation
      console.log('=== AI FORM GENERATION DEBUG ===');
      console.log('Original prompt length:', prompt.length);
      console.log('First 500 characters of prompt:', prompt.substring(0, 500));
      console.log('AI Generated Form Structure:', formStructure);
      console.log('Form structure type:', typeof formStructure.form_structure);
      console.log('Form structure is array:', Array.isArray(formStructure.form_structure));
      console.log('Number of fields:', formStructure.form_structure?.length || 0);
      console.log('Form title:', formStructure.title);
      console.log('Form description:', formStructure.description);
      if (formStructure.form_structure && formStructure.form_structure.length > 0) {
        console.log('First field:', formStructure.form_structure[0]);
        console.log('Last field:', formStructure.form_structure[formStructure.form_structure.length - 1]);
      }
      console.log('=== END DEBUG ===');
      
      onGenerateForm(formStructure, prompt);
      onClose();
      toast.success('Form generated successfully with AI!');
    } catch (error) {
      console.error('AI generation error:', error);
      
      // Fallback to local generation if AI service fails
      try {
        console.log('Falling back to local form generation...');
        const formStructure = generateFormFromPrompt(prompt);
        
        onGenerateForm(formStructure, prompt);
        onClose();
        toast.success('Form generated successfully (using fallback method)!');
      } catch (fallbackError) {
        toast.error('Failed to generate form. Please try again.');
        console.error('Fallback generation error:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  }, [prompt, onGenerateForm, onClose]);

  const generateFormFromPrompt = (promptText) => {
    const lowerPrompt = promptText.toLowerCase();
    
    console.log('=== GENERATE FORM FROM PROMPT ===');
    console.log('Prompt text length:', promptText.length);
    
    // Enhanced parsing for Word document content
    const lines = promptText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    console.log('Lines after split and filter:', lines.length);
    console.log('First few lines:', lines.slice(0, 5));
    
    // Try specialized parsing for food safety assessment first
    let fields = parseFoodSafetyAssessment(promptText);
    
    // If no fields found, try general document parsing
    if (fields.length === 0) {
      console.log('No fields from food safety parser, trying general parsing...');
      const extractedFields = parseDocumentContent(lines);
      fields = extractedFields;
    }
    
    // Final fallback
    if (fields.length === 0) {
      console.log('No fields from document parsing, using keyword fallback...');
      fields = generateFieldsFromKeywords(lowerPrompt);
    }
    
    console.log('Final fields count:', fields.length);
    
    return {
      title: extractTitle(promptText) || 'Generated Form',
      description: extractDescription(promptText),
      form_structure: Array.isArray(fields) ? fields : []
    };
  };

  // Function to parse document content and extract form fields
  const parseDocumentContent = (lines) => {
    console.log('=== PARSING DOCUMENT CONTENT ===');
    console.log('Total lines to process:', lines.length);
    
    const fields = [];
    let fieldIndex = 0;
    let currentQuestion = '';
    let currentOptions = [];
    let isCollectingOptions = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lowerLine = line.toLowerCase();
      
      // Debug: Log every line being processed
      if (i < 10 || line.includes('□') || line.includes('A.') || line.includes('1.')) {
        console.log(`Line ${i}:`, JSON.stringify(line));
      }
      
      // Skip empty lines and headers
      if (line.length < 3 || isHeader(line)) {
        if (line.length < 3) {
          console.log(`Skipping short line ${i}:`, JSON.stringify(line));
        } else {
          console.log(`Skipping header line ${i}:`, JSON.stringify(line));
        }
        continue;
      }
      
      // Check if this is a numbered question (starts with a number)
      const questionMatch = line.match(/^(\d+)\.?\s*(.+)/);
      if (questionMatch) {
        // If we were collecting options for a previous question, save it
        if (currentQuestion && currentOptions.length > 0) {
          fields.push(createMultipleChoiceField(currentQuestion, currentOptions, fieldIndex++));
        }
        
        // Start new question
        currentQuestion = questionMatch[2].trim();
        currentOptions = [];
        isCollectingOptions = true;
        
        // Check if this line also contains options
        if (lowerLine.includes('□') || lowerLine.includes('☐') || lowerLine.includes('☑')) {
          const field = parseMultipleChoiceQuestion(line, lowerLine, fieldIndex++);
          if (field) {
            fields.push(field);
            currentQuestion = '';
            currentOptions = [];
            isCollectingOptions = false;
          }
        }
        continue;
      }
      
      // If we're collecting options and this line has checkboxes
      if (isCollectingOptions && (lowerLine.includes('□') || lowerLine.includes('☐') || lowerLine.includes('☑'))) {
        const field = parseMultipleChoiceQuestion(line, lowerLine, fieldIndex++);
        if (field) {
          fields.push(field);
          currentQuestion = '';
          currentOptions = [];
          isCollectingOptions = false;
        }
        continue;
      }
      
      // If we're collecting options and this line starts with A., B., C., etc.
      if (isCollectingOptions && /^[A-Z]\.\s/.test(line)) {
        currentOptions.push(line.trim());
        continue;
      }
      
      // Detect other field types
      const field = detectFieldType(line, lowerLine, fieldIndex);
      if (field) {
        // If we were collecting options for a previous question, save it first
        if (currentQuestion && currentOptions.length > 0) {
          fields.push(createMultipleChoiceField(currentQuestion, currentOptions, fieldIndex++));
          currentQuestion = '';
          currentOptions = [];
          isCollectingOptions = false;
        }
        
        fields.push(field);
        fieldIndex++;
      }
    }
    
    // Handle any remaining question with options
    if (currentQuestion && currentOptions.length > 0) {
      fields.push(createMultipleChoiceField(currentQuestion, currentOptions, fieldIndex++));
    }
    
    return fields;
  };

  // Helper function to create multiple choice field
  const createMultipleChoiceField = (question, options, index) => {
    return {
      id: `mcq_${Date.now()}_${index}`,
      type: 'radio',
      label: question.replace(/[:\-•]/g, '').trim(),
      name: `mcq_${Date.now()}_${index}`,
      required: false,
      options: options
    };
  };

  // Function to detect field type from text
  const detectFieldType = (line, lowerLine, index) => {
    // Detect multiple choice questions with options on same line
    if (lowerLine.includes('□') || lowerLine.includes('☐') || lowerLine.includes('☑')) {
      return parseMultipleChoiceQuestion(line, lowerLine, index);
    }
    
    // Detect employee name fields (with dots/underscores)
    if (lowerLine.includes('employee name') || lowerLine.includes('name') && line.includes('…')) {
      return {
        id: `employee_name_${Date.now()}_${index}`,
        type: 'text',
        label: 'Employee Name',
        name: `employee_name_${Date.now()}_${index}`,
        required: true,
      };
    }
    
    // Detect employee number fields
    if (lowerLine.includes('employee number') || lowerLine.includes('emp number')) {
      return {
        id: `employee_number_${Date.now()}_${index}`,
        type: 'text',
        label: 'Employee Number',
        name: `employee_number_${Date.now()}_${index}`,
        required: true,
      };
    }
    
    // Detect designation fields
    if (lowerLine.includes('designation')) {
      return {
        id: `designation_${Date.now()}_${index}`,
        type: 'text',
        label: 'Designation',
        name: `designation_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect brand fields
    if (lowerLine.includes('brand')) {
      return {
        id: `brand_${Date.now()}_${index}`,
        type: 'text',
        label: 'Brand',
        name: `brand_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect date of training fields
    if (lowerLine.includes('date of training')) {
      return {
        id: `training_date_${Date.now()}_${index}`,
        type: 'date',
        label: 'Date of Training',
        name: `training_date_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect name fields (fallback)
    if (lowerLine.includes('name') && !lowerLine.includes('company') && !lowerLine.includes('organization')) {
      return {
        id: `name_${Date.now()}_${index}`,
        type: 'text',
        label: line.replace(/[:\-•…]/g, '').trim(),
        name: `name_${Date.now()}_${index}`,
        required: true,
      };
    }
    
    // Detect email fields
    if (lowerLine.includes('email') || lowerLine.includes('e-mail')) {
      return {
        id: `email_${Date.now()}_${index}`,
        type: 'email',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `email_${Date.now()}_${index}`,
        required: true,
        validation: {
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
          message: 'Please enter a valid email address'
        }
      };
    }
    
    // Detect phone fields
    if (lowerLine.includes('phone') || lowerLine.includes('mobile') || lowerLine.includes('contact')) {
      return {
        id: `phone_${Date.now()}_${index}`,
        type: 'text',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `phone_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect address fields
    if (lowerLine.includes('address')) {
      return {
        id: `address_${Date.now()}_${index}`,
        type: 'textarea',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `address_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect age fields
    if (lowerLine.includes('age')) {
      return {
        id: `age_${Date.now()}_${index}`,
        type: 'select',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `age_${Date.now()}_${index}`,
        required: false,
        options: ['18-25', '26-35', '36-45', '46-55', '56-65', '65+']
      };
    }
    
    // Detect gender fields
    if (lowerLine.includes('gender') || lowerLine.includes('sex')) {
      return {
        id: `gender_${Date.now()}_${index}`,
        type: 'radio',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `gender_${Date.now()}_${index}`,
        required: false,
        options: ['Male', 'Female', 'Other', 'Prefer not to say']
      };
    }
    
    // Detect rating/scale fields
    if (lowerLine.includes('rate') || lowerLine.includes('scale') || lowerLine.includes('satisfaction') || lowerLine.includes('rating') || lowerLine.includes('score')) {
      return {
        id: `rating_${Date.now()}_${index}`,
        type: 'rating',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `rating_${Date.now()}_${index}`,
        required: false,
        maxRating: 5,
        ratingLabels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent']
      };
    }
    
    // Detect yes/no questions
    if (lowerLine.includes('yes') || lowerLine.includes('no') || (lowerLine.includes('?') && line.length < 100)) {
      return {
        id: `yesno_${Date.now()}_${index}`,
        type: 'radio',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `yesno_${Date.now()}_${index}`,
        required: false,
        options: ['Yes', 'No']
      };
    }
    
    // Detect multiple choice questions
    if (lowerLine.includes('select') || lowerLine.includes('choose') || lowerLine.includes('option')) {
      return {
        id: `select_${Date.now()}_${index}`,
        type: 'select',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `select_${Date.now()}_${index}`,
        required: false,
        options: ['Option 1', 'Option 2', 'Option 3', 'Option 4']
      };
    }
    
    // Detect comment/feedback fields
    if (lowerLine.includes('comment') || lowerLine.includes('feedback') || lowerLine.includes('suggestion') || lowerLine.includes('additional')) {
      return {
        id: `comment_${Date.now()}_${index}`,
        type: 'textarea',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `comment_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect date fields
    if (lowerLine.includes('date') || lowerLine.includes('birthday') || lowerLine.includes('dob')) {
      return {
        id: `date_${Date.now()}_${index}`,
        type: 'date',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `date_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect file upload fields
    if (lowerLine.includes('upload') || lowerLine.includes('attach') || lowerLine.includes('document') || lowerLine.includes('resume') || lowerLine.includes('cv')) {
      return {
        id: `file_${Date.now()}_${index}`,
        type: 'file',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `file_${Date.now()}_${index}`,
        required: false,
        acceptedFileTypes: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
        maxFileSize: 10,
        multipleFiles: false
      };
    }
    
    // Detect company/organization fields
    if (lowerLine.includes('company') || lowerLine.includes('organization') || lowerLine.includes('employer')) {
      return {
        id: `company_${Date.now()}_${index}`,
        type: 'text',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `company_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect job title/position fields
    if (lowerLine.includes('job') || lowerLine.includes('position') || lowerLine.includes('title') || lowerLine.includes('role')) {
      return {
        id: `job_${Date.now()}_${index}`,
        type: 'text',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `job_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect experience/years fields
    if (lowerLine.includes('experience') || lowerLine.includes('years') || lowerLine.includes('level')) {
      return {
        id: `experience_${Date.now()}_${index}`,
        type: 'select',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `experience_${Date.now()}_${index}`,
        required: false,
        options: ['0-1 years', '2-3 years', '4-5 years', '6-10 years', '10+ years']
      };
    }
    
    // Detect website/url fields
    if (lowerLine.includes('website') || lowerLine.includes('url') || lowerLine.includes('link')) {
      return {
        id: `website_${Date.now()}_${index}`,
        type: 'url',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `website_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    // Detect checkbox fields for multiple selections
    if (lowerLine.includes('check') || lowerLine.includes('tick') || lowerLine.includes('mark all') || lowerLine.includes('select all')) {
      return {
        id: `checkbox_${Date.now()}_${index}`,
        type: 'checkbox',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `checkbox_${Date.now()}_${index}`,
        required: false,
        options: ['Option 1', 'Option 2', 'Option 3', 'Option 4']
      };
    }
    
    // Default text field for other questions
    if (line.includes('?') || line.length > 10) {
      return {
        id: `text_${Date.now()}_${index}`,
        type: 'text',
        label: line.replace(/[:\-•]/g, '').trim(),
        name: `text_${Date.now()}_${index}`,
        required: false,
      };
    }
    
    return null;
  };

  // Function to parse multiple choice questions with checkboxes
  const parseMultipleChoiceQuestion = (line, lowerLine, index) => {
    console.log(`=== PARSING MCQ Line ${index} ===`);
    console.log('Line content:', JSON.stringify(line));
    
    // Extract options from lines with checkboxes - improved regex
    const checkboxRegex = /([A-Z]\.\s*[^□☐☑]+?)\s*[□☐☑]/g;
    const options = [];
    let match;
    
    while ((match = checkboxRegex.exec(line)) !== null) {
      const optionText = match[1].trim();
      console.log('Found option:', JSON.stringify(optionText));
      if (optionText.length > 0 && optionText.length < 200) { // Reasonable option length
        options.push(optionText);
      }
    }
    
    console.log('Total options found:', options.length);
    console.log('Options:', options);
    
    if (options.length >= 2) { // Need at least 2 options for a valid MCQ
      // Try to extract the question text (before the first option)
      const firstOptionIndex = line.indexOf(options[0]);
      let questionText = line.substring(0, firstOptionIndex).trim();
      
      // Clean up the question text
      questionText = questionText.replace(/^\d+\.?\s*/, ''); // Remove question number
      questionText = questionText.replace(/[:\-•]/g, '').trim();
      
      // If no clear question text, use the first part of the line
      if (!questionText || questionText.length < 10) {
        questionText = line.split(/[A-Z]\./)[0].replace(/^\d+\.?\s*/, '').trim();
      }
      
      // Final fallback
      if (!questionText || questionText.length < 5) {
        questionText = `Question ${index + 1}`;
      }
      
      return {
        id: `mcq_${Date.now()}_${index}`,
        type: 'radio',
        label: questionText,
        name: `mcq_${Date.now()}_${index}`,
        required: false,
        options: options
      };
    }
    
    return null;
  };

  // Specialized parser for food safety assessment documents
  const parseFoodSafetyAssessment = (text) => {
    console.log('=== PARSING FOOD SAFETY ASSESSMENT ===');
    
    const fields = [];
    let fieldIndex = 0;
    
    // Add employee information fields first
    if (text.includes('EMPLOYEE NAME') || text.includes('employee name')) {
      fields.push({
        id: `employee_name_${Date.now()}_${fieldIndex++}`,
        type: 'text',
        label: 'Employee Name',
        name: `employee_name_${Date.now()}_${fieldIndex}`,
        required: true,
      });
    }
    
    if (text.includes('EMPLOYEE NUMBER') || text.includes('employee number')) {
      fields.push({
        id: `employee_number_${Date.now()}_${fieldIndex++}`,
        type: 'text',
        label: 'Employee Number',
        name: `employee_number_${Date.now()}_${fieldIndex}`,
        required: true,
      });
    }
    
    if (text.includes('DESIGNATION') || text.includes('designation')) {
      fields.push({
        id: `designation_${Date.now()}_${fieldIndex++}`,
        type: 'text',
        label: 'Designation',
        name: `designation_${Date.now()}_${fieldIndex}`,
        required: false,
      });
    }
    
    if (text.includes('BRAND') || text.includes('brand')) {
      fields.push({
        id: `brand_${Date.now()}_${fieldIndex++}`,
        type: 'text',
        label: 'Brand',
        name: `brand_${Date.now()}_${fieldIndex}`,
        required: false,
      });
    }
    
    if (text.includes('DATE OF TRAINING') || text.includes('date of training')) {
      fields.push({
        id: `training_date_${Date.now()}_${fieldIndex++}`,
        type: 'date',
        label: 'Date of Training',
        name: `training_date_${Date.now()}_${fieldIndex}`,
        required: false,
      });
    }
    
    // Parse multiple choice questions
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Look for numbered questions
      const questionMatch = line.match(/^(\d+)\.?\s*(.+)/);
      if (questionMatch) {
        const questionNumber = questionMatch[1];
        let questionText = questionMatch[2].trim();
        
        // Look for options in the same line or next lines
        const options = [];
        
        // Check current line for options with checkboxes
        const checkboxMatches = line.match(/([A-Z]\.\s*[^□☐☑]+?)\s*[□☐☑]/g);
        if (checkboxMatches) {
          checkboxMatches.forEach(match => {
            const option = match.replace(/\s*[□☐☑]/g, '').trim();
            if (option.length > 0) {
              options.push(option);
            }
          });
        }
        
        // If no options found on same line, check next lines
        if (options.length === 0) {
          let j = i + 1;
          while (j < lines.length && j < i + 5) { // Look ahead max 5 lines
            const nextLine = lines[j];
            
            // Stop if we hit another numbered question
            if (nextLine.match(/^\d+\.?\s/)) {
              break;
            }
            
            // Look for options in this line
            const nextLineOptions = nextLine.match(/([A-Z]\.\s*[^□☐☑]+?)\s*[□☐☑]/g);
            if (nextLineOptions) {
              nextLineOptions.forEach(match => {
                const option = match.replace(/\s*[□☐☑]/g, '').trim();
                if (option.length > 0) {
                  options.push(option);
                }
              });
            }
            
            j++;
          }
        }
        
        // If we found options, create the field
        if (options.length >= 2) {
          // Clean up question text
          questionText = questionText.replace(/[:\-•]/g, '').trim();
          
          fields.push({
            id: `question_${questionNumber}_${Date.now()}_${fieldIndex++}`,
            type: 'radio',
            label: `Question ${questionNumber}: ${questionText}`,
            name: `question_${questionNumber}_${Date.now()}_${fieldIndex}`,
            required: false,
            options: options
          });
          
          console.log(`Created question ${questionNumber}:`, questionText);
          console.log('Options:', options);
        }
      }
    }
    
    console.log(`Food safety parser found ${fields.length} fields`);
    return fields;
  };

  // Helper function to check if line is a header
  const isHeader = (line) => {
    const upperCaseRatio = (line.match(/[A-Z]/g) || []).length / line.length;
    return upperCaseRatio > 0.7 || line.length < 5;
  };

  // Function to extract title from document
  const extractTitle = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    if (lines.length > 0) {
      const firstLine = lines[0];
      // If first line looks like a title (short, no special characters)
      if (firstLine.length < 50 && !firstLine.includes('?') && !firstLine.includes(':')) {
        return firstLine;
      }
    }
    return null;
  };

  // Function to extract description from document
  const extractDescription = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    // Look for description in first few lines
    for (let i = 0; i < Math.min(3, lines.length); i++) {
      const line = lines[i];
      if (line.length > 20 && line.length < 200 && !line.includes('?')) {
        return line;
      }
    }
    return '';
  };

  // Fallback function for keyword-based generation
  const generateFieldsFromKeywords = (lowerPrompt) => {
    let fields = [];
    
    // Add name field if mentioned
    if (lowerPrompt.includes('name') || lowerPrompt.includes('personal')) {
      fields.push({
        id: `name_${Date.now()}`,
        type: 'text',
        label: 'Full Name',
        name: `name_${Date.now()}`,
        required: true,
      });
    }
    
    // Add email field if mentioned
    if (lowerPrompt.includes('email') || lowerPrompt.includes('contact')) {
      fields.push({
        id: `email_${Date.now()}`,
        type: 'email',
        label: 'Email Address',
        name: `email_${Date.now()}`,
        required: true,
        validation: {
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
          message: 'Please enter a valid email address'
        }
      });
    }
    
    // Add phone field if mentioned
    if (lowerPrompt.includes('phone') || lowerPrompt.includes('contact')) {
      fields.push({
        id: `phone_${Date.now()}`,
        type: 'text',
        label: 'Phone Number',
        name: `phone_${Date.now()}`,
        required: false,
        validation: {
          pattern: '^[\\+]?[1-9][\\d]{0,15}$',
          message: 'Please enter a valid phone number'
        }
      });
    }
    
    // Add rating field if mentioned
    if (lowerPrompt.includes('rating') || lowerPrompt.includes('satisfaction') || lowerPrompt.includes('feedback')) {
      fields.push({
        id: `rating_${Date.now()}`,
        type: 'rating',
        label: 'Rating',
        name: `rating_${Date.now()}`,
        required: false,
        maxRating: 5,
        ratingLabels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent']
      });
    }
    
    // Add textarea for comments/message
    if (lowerPrompt.includes('comment') || lowerPrompt.includes('message') || lowerPrompt.includes('feedback')) {
      fields.push({
        id: `message_${Date.now()}`,
        type: 'textarea',
        label: 'Comments/Message',
        name: `message_${Date.now()}`,
        required: false,
      });
    }
    
    // If no specific fields detected, add basic fields
    if (fields.length === 0) {
      fields = [
        {
          id: `name_${Date.now()}`,
          type: 'text',
          label: 'Name',
          name: `name_${Date.now()}`,
          required: true,
        },
        {
          id: `email_${Date.now()}`,
          type: 'email',
          label: 'Email',
          name: `email_${Date.now()}`,
          required: true,
        },
        {
          id: `message_${Date.now()}`,
          type: 'textarea',
          label: 'Message',
          name: `message_${Date.now()}`,
          required: true,
        }
      ];
    }
    
    return fields;
  };

  const handleClose = () => {
    if (!loading) {
      setPrompt('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose} disabled={loading}>
          <FiX />
        </CloseButton>
        
        <ModalTitle>
          <FiZap />
          AI Form Generator
        </ModalTitle>
        
        <ModalDescription>
          Describe the form you want to create and AI will generate it for you:
        </ModalDescription>
        
        <ModeSelector>
          <ModeButton 
            active={uploadMode === 'text'} 
            onClick={() => setUploadMode('text')}
            disabled={loading}
          >
            <FiFileText />
            Type Description
          </ModeButton>
          <ModeButton 
            active={uploadMode === 'file'} 
            onClick={() => setUploadMode('file')}
            disabled={loading}
          >
            <FiUpload />
            Upload Word File
          </ModeButton>
        </ModeSelector>
        
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>AI is generating your form...</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            {uploadMode === 'text' ? (
              <>
                <PromptContainer>
                  <PromptLabel htmlFor="prompt">Describe your form:</PromptLabel>
                  <PromptTextarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., Create a customer feedback form with rating, comments, and contact information"
                    disabled={loading}
                  />
                </PromptContainer>
              </>
            ) : (
              <FileUploadContainer>
                <FileUploadArea 
                  uploading={uploading}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <FileUploadIcon>
                    {uploading ? '⏳' : <FiUpload />}
                  </FileUploadIcon>
                  <FileUploadText>
                    {uploading ? 'Processing document...' : 'Click to upload Word document'}
                  </FileUploadText>
                  <FileUploadSubtext>
                    Supports .doc, .docx, and .txt files (max 10MB)
                  </FileUploadSubtext>
                  <FileInput
                    id="file-input"
                    type="file"
                    accept=".doc,.docx,.txt"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </FileUploadArea>
                
                {uploadedFile && (
                  <UploadedFileInfo>
                    <FileIcon>
                      <FiFileText />
                    </FileIcon>
                    <FileDetails>
                      <FileName>{uploadedFile.name}</FileName>
                      <FileSize>{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</FileSize>
                    </FileDetails>
                    <RemoveFileButton 
                      onClick={() => {
                        setUploadedFile(null);
                        setPrompt('');
                      }}
                      title="Remove file"
                    >
                      <FiX />
                    </RemoveFileButton>
                  </UploadedFileInfo>
                )}
                
                {prompt && uploadMode === 'file' && (
                  <PromptContainer style={{ marginTop: '16px' }}>
                    <PromptLabel>Extracted Content:</PromptLabel>
                    <PromptTextarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      style={{ minHeight: '200px' }}
                      disabled={loading}
                    />
                  </PromptContainer>
                )}
              </FileUploadContainer>
            )}
            
            <ButtonContainer>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleGenerate} disabled={loading || !prompt.trim()}>
                <FiZap />
                Generate Form
              </Button>
            </ButtonContainer>
          </>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
}

export default AIGeneratorModal;